import React, { useMemo } from "react";
import { Table } from "../../../../../shared/Table/Table";
import classes from "../../../newBusinessInsightsContent.module.scss";
import { useTotalSalesSummaryV2 } from "./hooks/useTotalSalesSummaryV2";
import Header from "../../../components/Header";
import { useHeaderActions } from "../../../hooks/useHeaderActions";
import { Box } from "../../../../../shared/Box/Box";
import { Skeleton } from "../../../../../shared/Skeleton/Skeleton";
import { Empty } from "../../../../../shared/Empty/Empty";
import { HTTP_ENDPOINTS } from "../../../../../consts/api";

const modalInfo = {
  title: "totalSalesSummaryReport.info.title",
  rows: [
    "totalSalesSummaryReport.info.clinicSummary",
    "totalSalesSummaryReport.info.providerSummary",
  ],
};

const TotalSalesSummaryV2 = () => {
  const { selectedClinic, period, apiPeriod } = useHeaderActions();
  const requestParams = {
    fromDate: apiPeriod.fromDate,
    toDate: apiPeriod.toDate,
    clinic_id:
      selectedClinic.value.value !== 0 ? [selectedClinic.value.value] : [],
  };
  const { data, isLoading } = useTotalSalesSummaryV2(requestParams);

  const isClinicDataEmpty = !isLoading && !data?.clinicTotalSales?.length;
  const isProviderDataEmpty = !isLoading && !data?.providerTotalSales?.length;

  const clinicData = useMemo(
    () =>
      data?.clinicTotalSales?.map((clinic) => ({
        clinicName: clinic.clinic_name,
        retailSales: clinic.retail_sales,
        serviceSales: clinic.service_sales,
        membershipSales: clinic.membership_sales,
        depositSales: clinic.deposit_sales,
        cancellationSales: clinic.cancellation_sales,
        egiftcardSales: clinic.egiftcard_sales,
        walletCredits: clinic.wallet_credits,
        totalSales: clinic.total_sales,
      })) || [],
    [data],
  );

  const providerData = useMemo(
    () =>
      data?.providerTotalSales?.map((provider) => ({
        providerName: provider.provider_name,
        retailSales: provider.retail_sales,
        serviceSales: provider.service_sales,
        membershipSales: provider.membership_sales,
        depositSales: provider.deposit_sales,
        cancellationSales: provider.cancellation_sales,
        egiftcardSales: provider.egiftcard_sales,
        walletCredits: provider.wallet_credits,
        totalSales: provider.total_sales,
      })) || [],
    [data],
  );

  const clinics =
    useMemo(
      () =>
        data?.clinics?.map((clinic) => ({
          label: clinic.clinic_name,
          value: clinic.id,
        })),
      [data],
    ) || [];

  const tableColumns = [
    { data: "Retail Sales", accessor: "retailSales" },
    { data: "Service Sales", accessor: "serviceSales" },
    { data: "Membership Sales", accessor: "membershipSales" },
    { data: "Deposit Sales", accessor: "depositSales" },
    { data: "Cancellation Sales", accessor: "cancellationSales" },
    { data: "eGift Card Sales", accessor: "egiftcardSales" },
    { data: "Wallet Credits", accessor: "walletCredits" },
    { data: "Total Sales", accessor: "totalSales" },
  ];

  return (
    <div className="flex-col font-14 gap-16">
      <Header
        title="Total Sales Summary Report V2"
        selectedClinic={selectedClinic}
        clinics={clinics}
        period={period}
        downloadRoute={HTTP_ENDPOINTS.getTotalSalesSummaryV2()}
        requestParams={requestParams}
        modalInfo={modalInfo}
      />
      <div className="flex-col gap-16">
        <div className={classes.tableContainer}>
          <Table
            bgColor="white"
            cols={[
              { data: "Clinic Name", accessor: "clinicName" },
              ...tableColumns,
            ]}
            data={clinicData}
          />
          {isLoading && (
            <Box>
              <Skeleton count={8} height={40.5} borderRadius="0px" />
            </Box>
          )}
          {isClinicDataEmpty && (
            <Box>
              <Empty position="center" />
            </Box>
          )}
        </div>
        <div className={classes.tableContainer}>
          <Table
            bgColor="white"
            cols={[
              { data: "Provider Name", accessor: "providerName" },
              ...tableColumns,
            ]}
            data={providerData}
          />
          {isLoading && (
            <Box>
              <Skeleton count={8} height={40.5} borderRadius="0px" />
            </Box>
          )}
          {isProviderDataEmpty && (
            <Box>
              <Empty position="center" />
            </Box>
          )}
        </div>
      </div>
    </div>
  );
};

export default TotalSalesSummaryV2;
